import React, { useState, useEffect, useRef, useCallback } from 'react';
import MeseroOrderPanel from './MeseroOrderPanel';
import MeseroGalleryView from './MeseroGalleryView';
import Button from 'react-bootstrap/Button';
import './MeseroOrdersShell.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ordersApi from './../../../api/ordersApi';
import platillosApi from './../../../api/platillosApi';
import comandasApi from './../../../api/comandasApi';
import {
    fetchComandasGroupedByOrder,
    parseOrderIdFromComandaSocketMsg,
} from './meseroComandasCache';
import { filterComandasAfterBorrado } from '../borradoSyncUtils';

import OrdenesCocina from './../MeseroPage/OrdenesCocinaComponent';
import Counter30To0 from '../Global/CounterComponent';
import Calendar from 'react-calendar/dist/esm/Calendar.js';
import 'react-calendar/dist/Calendar.css';

import io from 'socket.io-client';
import { loadGalleryViewPreference, saveGalleryViewPreference, loadOrdersViewDate, saveOrdersViewDate, clearOrdersViewDate } from './meseroViewCache';

const socket = io(`${process.env.REACT_APP_API_URL}`);

const MeseroOrdersShell = ({ modeInterface }) => {
    const notify = (message) => toast(message);
    const bodyScrollLockRef = useRef({ overflow: '', paddingRight: '' });
    const fetchOrdersTimerRef = useRef(null);
    const syncComandasTimerRef = useRef(null);
    const ordersRef = useRef([]);
    const [orders, setOrders] = useState([]);
    const [comandasByOrder, setComandasByOrder] = useState({});
    const [numOrders, setNumOrders] = useState(0);
    const [platillos, setPlatillos] = useState([]);
    const [numPlatillos, setNumPlatillos] = useState(0);
    const [ComandasPerScreen, setComandasPerScreen] = useState(3);
    const [slide, setSlide] = useState(1);
    const [galleryView, setGalleryView] = useState(() => (
        modeInterface ? loadGalleryViewPreference() : false
    ));
    const [ordersViewDate, setOrdersViewDate] = useState(() => (
        modeInterface ? (loadOrdersViewDate() || new Date()) : new Date()
    ));
    const [showOrdersDayPicker, setShowOrdersDayPicker] = useState(false);
    // Debug panel for orders GET — disabled, keep for future troubleshooting
    // const [showOrdersFetchDebug, setShowOrdersFetchDebug] = useState(true);
    // const [ordersFetchDebug, setOrdersFetchDebug] = useState({
    //     status: 'idle',
    //     trigger: null,
    //     selectorDate: null,
    //     queryDate: null,
    //     queryOffset: null,
    //     galleryView: false,
    //     requests: [],
    //     mergedCount: 0,
    //     mergedOrderIds: [],
    //     error: null,
    //     executedAt: null,
    // });
    const galleryViewRef = useRef(galleryView);
    const ordersViewDateRef = useRef(ordersViewDate);
    const ordersFetchInitializedRef = useRef(false);

    const setGalleryViewPersisted = (isGallery) => {
        setGalleryView(isGallery);
        if (modeInterface) {
            saveGalleryViewPreference(isGallery);
        }
    };

    useEffect(() => {
        ordersRef.current = orders;
    }, [orders]);

    useEffect(() => {
        galleryViewRef.current = galleryView;
    }, [galleryView]);

    useEffect(() => {
        ordersViewDateRef.current = ordersViewDate;
    }, [ordersViewDate]);

    const syncComandasCache = async (orderList) => {
        try {
            const grouped = await fetchComandasGroupedByOrder(orderList);
            setComandasByOrder(grouped);
        } catch (err) {
            console.log(err);
        }
    };

    const syncComandaForOrder = async (orderId) => {
        const key = Number(orderId);
        if (!Number.isFinite(key)) return;
        try {
            const comandas = await comandasApi.getComandasByOrderId(key);
            setComandasByOrder((prev) => ({ ...prev, [key]: comandas }));
        } catch (err) {
            console.log(err);
        }
    };

    const syncComandaForOrderTimerRef = useRef({});

    const scheduleSyncComandaForOrder = (orderId) => {
        const key = String(orderId);
        if (syncComandaForOrderTimerRef.current[key]) {
            clearTimeout(syncComandaForOrderTimerRef.current[key]);
        }
        syncComandaForOrderTimerRef.current[key] = setTimeout(() => {
            delete syncComandaForOrderTimerRef.current[key];
            syncComandaForOrder(orderId);
        }, 400);
    };

    const syncComandasCacheEntry = useCallback((orderId, comandas) => {
        const key = Number(orderId);
        if (!Number.isFinite(key)) return;
        const nextComandas = Array.isArray(comandas) ? comandas : [];
        setComandasByOrder((prev) => {
            if (prev[key] === nextComandas) {
                return prev;
            }
            return {
                ...prev,
                [key]: nextComandas,
            };
        });
    }, []);

    const syncOrderCacheEntry = useCallback((orderPatch) => {
        const key = Number(orderPatch?.OrderID);
        if (!Number.isFinite(key)) return;
        setOrders((prev) => {
            let changed = false;
            const next = prev.map((order) => {
                if (Number(order.OrderID) !== key) {
                    return order;
                }
                const patchKeys = Object.keys(orderPatch);
                const isUnchanged = patchKeys.every(
                    (field) => JSON.stringify(order[field]) === JSON.stringify(orderPatch[field]),
                );
                if (isUnchanged) {
                    return order;
                }
                changed = true;
                return { ...order, ...orderPatch };
            });
            return changed ? next : prev;
        });
    }, []);

    const syncOrderFromServerTimerRef = useRef({});

    const syncOrderFromServer = async (orderId) => {
        const key = Number(orderId);
        if (!Number.isFinite(key)) return;
        try {
            const order = await ordersApi.getOrder(key);
            syncOrderCacheEntry(order);
        } catch (err) {
            console.log(err);
        }
    };

    const scheduleSyncOrderFromServer = (orderId) => {
        const timerKey = String(orderId);
        if (syncOrderFromServerTimerRef.current[timerKey]) {
            clearTimeout(syncOrderFromServerTimerRef.current[timerKey]);
        }
        syncOrderFromServerTimerRef.current[timerKey] = setTimeout(() => {
            delete syncOrderFromServerTimerRef.current[timerKey];
            syncOrderFromServer(orderId);
        }, 400);
    };

    const scheduleSyncComandasCache = (orderList = ordersRef.current) => {
        if (syncComandasTimerRef.current) {
            clearTimeout(syncComandasTimerRef.current);
        }
        syncComandasTimerRef.current = setTimeout(() => {
            syncComandasTimerRef.current = null;
            syncComandasCache(orderList);
        }, 150);
    };

    const toLocalYMD = (dt) => {
        const d = dt instanceof Date ? dt : new Date(dt);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const isOrdersViewToday = (date = ordersViewDateRef.current) => (
        toLocalYMD(date) === toLocalYMD(new Date())
    );

    const getOrdersDateQuery = () => {
        if (!modeInterface || galleryViewRef.current) {
            return null;
        }
        return {
            date: toLocalYMD(ordersViewDateRef.current),
            offset: -new Date().getTimezoneOffset() / 60,
        };
    };

    // const buildOrdersFetchDebugRequest = (status, dateQuery) => {
    //     const base = `${process.env.REACT_APP_API_URL}/api/orders/getByOrderCustStatus/${status}`;
    //     if (!dateQuery) {
    //         return { status, mode: 'default-today', url: base };
    //     }
    //     return {
    //         status,
    //         mode: 'by-date',
    //         url: `${base}?date=${dateQuery.date}&offset=${dateQuery.offset}`,
    //         date: dateQuery.date,
    //         offset: dateQuery.offset,
    //     };
    // };

    const fetchOrdersByStatus = (status) => {
        const dateQuery = getOrdersDateQuery();
        if (dateQuery) {
            return ordersApi.getOrdersByOrderCustStatus(status, dateQuery.date, dateQuery.offset);
        }
        return ordersApi.getOrdersByOrderCustStatus(status);
    };

    const fetchOrders = async () => {
        if (modeInterface) {
            try {
                const [doneOrders, inPlaceOrders] = await Promise.all([
                    fetchOrdersByStatus('Done'),
                    fetchOrdersByStatus('InPlace'),
                ]);
                const mergedOrders = [...doneOrders, ...inPlaceOrders];
                setOrders(mergedOrders);
                setNumOrders(mergedOrders.length);
                syncComandasCache(mergedOrders);
            } catch (err) {
                console.log(err);
                notify(`Error al cargar las comandas: ${err}`);
            }
            return;
        }

        try {
            const data = await ordersApi.getOrdersByOrderCustStatus('InPlace');
            if (data.length <= 3) {
                setComandasPerScreen(3);
            } else if (data.length === 4) {
                setComandasPerScreen(4);
            } else if (data.length > 4) {
                setComandasPerScreen(6);
            }
            setOrders(data);
            setNumOrders(data.length);
        } catch (err) {
            console.log(err);
            notify(`Error al cargar las comandas: ${err}`);
        }
    };

    /* Debug fetchOrders with panel state — re-enable with SHOW_ORDERS_FETCH_DEBUG UI block
    const fetchOrders = async (trigger = 'initial') => {
        const dateQuery = getOrdersDateQuery();
        const debugBase = { trigger, selectorDate: toLocalYMD(ordersViewDateRef.current), ... };
        setOrdersFetchDebug((prev) => ({ ...prev, status: 'loading', ...debugBase }));
        ...
    };
    */

    const scheduleFetchOrders = () => {
        if (fetchOrdersTimerRef.current) {
            clearTimeout(fetchOrdersTimerRef.current);
        }
        fetchOrdersTimerRef.current = setTimeout(() => {
            fetchOrdersTimerRef.current = null;
            fetchOrders();
        }, 120);
    };
    
    const fetchPlatillos = () => {
        platillosApi.getPlatillos()
        .then(data => {
            setPlatillos(prevPlatillos => {return (data);});
            setNumPlatillos(prevNumPlatillos => {return data.length;});
        })
        .catch(err => {
            console.log(err);
            notify(`Error al cargar las comandas: ${err}`);
            // alert("Error al cargar las comandas");
        });
    };

    const refreshAllData = async () => {
        try {
            const [doneOrders, inPlaceOrders, platillosData] = await Promise.all([
                ordersApi.getOrdersByOrderCustStatus('Done'),
                ordersApi.getOrdersByOrderCustStatus('InPlace'),
                platillosApi.getPlatillos(),
            ]);
            const mergedOrders = [...doneOrders, ...inPlaceOrders];
            setOrders(mergedOrders);
            setNumOrders(mergedOrders.length);
            setPlatillos(platillosData);
            setNumPlatillos(platillosData.length);
            await syncComandasCache(mergedOrders);
            notify('Datos actualizados');
        } catch (err) {
            console.log(err);
            notify(`Error al refrescar: ${err}`);
            throw err;
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { // fetchOrders
        fetchOrders();
        fetchPlatillos();
    }, []);

    useEffect(() => {
        if (!modeInterface) return;
        if (!ordersFetchInitializedRef.current) {
            ordersFetchInitializedRef.current = true;
            return;
        }
        fetchOrders();
        setSlide(1);
    }, [galleryView, modeInterface]);

    const handleOrdersViewDateChange = (nextDate) => {
        setOrdersViewDate(nextDate);
        ordersViewDateRef.current = nextDate;
        if (isOrdersViewToday(nextDate)) {
            clearOrdersViewDate();
        } else {
            saveOrdersViewDate(nextDate);
        }
        setShowOrdersDayPicker(false);
        fetchOrders();
        setSlide(1);
    };

    const resetOrdersViewDateToToday = () => {
        const today = new Date();
        setOrdersViewDate(today);
        ordersViewDateRef.current = today;
        clearOrdersViewDate();
        setShowOrdersDayPicker(false);
        fetchOrders();
        setSlide(1);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!modeInterface) {
            const hacerAlgo = () => {
                fetchOrders();
            };
            const intervalo = setInterval(hacerAlgo, 5000);
            return () => clearInterval(intervalo);
        }
    }, []); // El array vacío asegura que el efecto se ejecute solo una vez al montar el componente

    const handleSlideChange = (newSlide) => {
        const maxSlide = numOrders;
        if (newSlide > 0 && newSlide <= maxSlide) {
            setSlide(prevSlide => {return(newSlide);})
        }
    };
    
    const handleOrderCustStatus = (OrderID, Status) => {
        const existingOrder = orders.find((order) => order.OrderID === OrderID);
        if (!existingOrder) return;
        const previousStatus = existingOrder.OrderCustStatus;
        const newOrder = { ...existingOrder, OrderCustStatus: Status };
        syncOrderCacheEntry(newOrder);
        ordersApi.updateOrder(newOrder)
        .then(() => {
            SocketUpdateOrder(OrderID);
        })
        .catch(err => {
            console.log(err);
            syncOrderCacheEntry({ ...existingOrder, OrderCustStatus: previousStatus });
            notify(`Error al actualizar una comanda: ${err}`);
        });
    }
    const renderOrders = () => {
        const OrdersArray = [];
        if (modeInterface) {
            if (galleryView) {
                return (
                    <MeseroGalleryView
                        orders={orders}
                        comandasByOrder={comandasByOrder}
                        onComandasCacheSync={syncComandasCacheEntry}
                        onOrderCacheSync={syncOrderCacheEntry}
                        platillos={platillos}
                        handleDeleteOrder={handleDeleteOrder}
                        handleOrderCustStatus={handleOrderCustStatus}
                        onRefreshAll={refreshAllData}
                        onNewOrder={handleNewOrderClick}
                        onSwitchToClassicView={() => setGalleryViewPersisted(false)}
                    />
                );
            }
            const start = (numOrders - (slide - 1));
            const end = Math.max((numOrders - (slide + (modeInterface ? 100 : ComandasPerScreen) - 1)), 0);
            for (let i = start - 1; i >= end; i--) {
                let iInterfaceFlag = false;
                if (i === start - 1) {
                    iInterfaceFlag = true
                }
                OrdersArray.push(
                <div key={orders[i].OrderID} className={`col-xl-${12/ComandasPerScreen} d-flex justify-content-center`}>
                    <MeseroOrderPanel modeInterface={modeInterface} iInterface={iInterfaceFlag} OrderID={orders[i].OrderID}
                    DeleteOrder={handleDeleteOrder}
                    handleOrderCustStatus={handleOrderCustStatus}
                    platillos={platillos}
                    numPlatillos={numPlatillos}
                    />
                </div>
                )
            }
            return (
                <div className="row">
                    {OrdersArray}
                </div>
                );
        }
        else {
            return (<OrdenesCocina modeInterface={modeInterface} Orders={orders} />)
        }
    };

    const handleNewOrderClick = () => ordersApi.getLastOrderID()
        .then((data) => {
            const newOrderId = data + 1;
            const newOrder = {
                OrderID: newOrderId,
                OrderPaidStatus: "Pending", // Partial-Paid
                OrderPrepStatus: "Preparing", // Served-Done
                OrderCustStatus: "InPlace", // BeBack-Done
                Customer: "",
                CuentaTotal: 0,
                ComandasList: []
            };
            return ordersApi.addOrder(newOrder)
                .then(() => {
                    setOrders((prevOrders) => [...prevOrders, newOrder]);
                    setNumOrders((prevNumOrders) => prevNumOrders + 1);
                    syncComandasCacheEntry(newOrderId, []);
                    SocketNewOrder();
                    const audio = new Audio("ComandaAudios/Pedido.wav");
                    audio.play();
                    return newOrderId;
                });
        })
        .catch((err) => {
            console.log(err);
            notify(`Error al crear la orden: ${err}`);
            return null;
        });
    const handleDeleteOrder = (OrderID) => {
        const confirm = window.confirm("Eliminar orden");
            if (confirm) {
                ordersApi.deleteOrder(OrderID)
                .then(() => {
                    const key = Number(OrderID);
                    setOrders(prevOrders => prevOrders.filter(order => order.OrderID !== OrderID));
                    setNumOrders(prevNumOrders => prevNumOrders - 1);
                    setComandasByOrder((prev) => {
                        if (!Number.isFinite(key)) return prev;
                        const next = { ...prev };
                        delete next[key];
                        return next;
                    });
                    SocketDeleteOrder();
                })
                .catch(err => {
                    console.log(err);
                    notify(`Error al eliminar orden: ${err}`);
                    // alert("Error al eliminar una comanda");
                });
            }
    };
    
    const SocketNewOrder = () => {
        socket.emit('NuevaOrdenDesdeCliente', {});
    };
    const SocketDeleteOrder = () => {
        socket.emit('OrdenEliminadaDesdeCliente', {});
    };
    const SocketUpdateOrder = (OrderID) => {
        socket.emit('OrdenActualizadaDesdeCliente', {msg: OrderID});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        return () => {
            if (fetchOrdersTimerRef.current) {
                clearTimeout(fetchOrdersTimerRef.current);
            }
            if (syncComandasTimerRef.current) {
                clearTimeout(syncComandasTimerRef.current);
            }
            Object.values(syncComandaForOrderTimerRef.current).forEach(clearTimeout);
            syncComandaForOrderTimerRef.current = {};
            Object.values(syncOrderFromServerTimerRef.current).forEach(clearTimeout);
            syncOrderFromServerTimerRef.current = {};
        };
    }, []);

    useEffect(() => { //Socket NewOrder
        const onNuevaOrden = (data) => {
            console.log("Mensaje: ", data)
            scheduleFetchOrders();
            if (!modeInterface) {
                const audio = new Audio("ComandaAudios/Pedido.wav");
                audio.play();
            }
        };
        const onOrdenEliminada = (data) => {
            console.log("Mensaje: ", data)
            scheduleFetchOrders();
        };
        const onOrdenActualizada = (data) => {
            console.log("OrdenActualizadaDesdeServidor Mensaje: ", data);
            const orderId = Number(data?.msg);
            if (Number.isFinite(orderId)) {
                scheduleSyncComandaForOrder(orderId);
                scheduleSyncOrderFromServer(orderId);
                return;
            }
            scheduleFetchOrders();
        };
        const onDeleteComanda = (data) => {
            const orderId = parseOrderIdFromComandaSocketMsg(data?.msg);
            if (orderId) {
                scheduleSyncComandaForOrder(orderId);
                scheduleSyncOrderFromServer(orderId);
                return;
            }
            scheduleFetchOrders();
        };
        const onBorradoAprobado = (payload) => {
            const orderId = Number(payload?.OrderID);
            if (!Number.isFinite(orderId)) {
                return;
            }
            setComandasByOrder((prev) => {
                const key = orderId;
                const current = prev[key] ?? prev[String(key)] ?? [];
                if (!Array.isArray(current) || current.length === 0) {
                    return prev;
                }
                const nextComandas = filterComandasAfterBorrado(current, payload);
                if (nextComandas.length === current.length) {
                    return prev;
                }
                return { ...prev, [key]: nextComandas };
            });
            scheduleSyncComandaForOrder(orderId);
            scheduleSyncOrderFromServer(orderId);
        };
        const onComandaChanged = (data) => {
            const orderId = parseOrderIdFromComandaSocketMsg(data?.msg);
            if (orderId) {
                scheduleSyncComandaForOrder(orderId);
                return;
            }
            scheduleSyncComandasCache();
        };

        socket.on('NuevaOrdenDesdeServidor', onNuevaOrden);
        socket.on('OrdenEliminadaDesdeServidor', onOrdenEliminada);
        socket.on('OrdenActualizadaDesdeServidor', onOrdenActualizada);
        socket.on('DeleteComandaDesdeServidor', onDeleteComanda);
        socket.on('BorradoAprobadoDesdeServidor', onBorradoAprobado);
        socket.on('NuevaComandaDesdeServidor', onComandaChanged);
        socket.on('UpdateComandaDesdeServidor', onComandaChanged);

        return () => {
            socket.off('NuevaOrdenDesdeServidor', onNuevaOrden);
            socket.off('OrdenEliminadaDesdeServidor', onOrdenEliminada);
            socket.off('OrdenActualizadaDesdeServidor', onOrdenActualizada);
            socket.off('DeleteComandaDesdeServidor', onDeleteComanda);
            socket.off('BorradoAprobadoDesdeServidor', onBorradoAprobado);
            socket.off('NuevaComandaDesdeServidor', onComandaChanged);
            socket.off('UpdateComandaDesdeServidor', onComandaChanged);
        };
    }, []);

    const [reloadFlag, setReloadFlag] = useState(false);

    const handleReloadFlag = () => {
        setReloadFlag(true);
    }

    // NUEVA FUNCIONALIDAD: Estados y funciones para el input de password
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordInput, setPasswordInput] = useState("");
    const passwordInputRef = useRef(null);
    const [passwordTarget, setPasswordTarget] = useState('calendar'); // 'calendar' | 'balance'
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    
    // Estados para el modal de Balance (ZonaYummy)
    const [showBalanceModal, setShowBalanceModal] = useState(false);
    const [balanceDate, setBalanceDate] = useState(new Date());
    const [balanceOrders, setBalanceOrders] = useState([]);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [showBalanceDayPicker, setShowBalanceDayPicker] = useState(false);
    
    // Estados para el submodal de edición de pagos
    const [editingOrder, setEditingOrder] = useState(null); // orden seleccionada para editar
    const [showEditPaymentsModal, setShowEditPaymentsModal] = useState(false);
    const [editingPaymentId, setEditingPaymentId] = useState(null); // pago en edición de monto
    const [editingPaymentAmount, setEditingPaymentAmount] = useState(0);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [monthlyActiveStartDate, setMonthlyActiveStartDate] = useState(new Date());
    const [dailySums, setDailySums] = useState({});
    const [sumsLoading, setSumsLoading] = useState(false);
    const [sumsProgress, setSumsProgress] = useState({ done: 0, total: 0 });
    const [dailySalesRows, setDailySalesRows] = useState([]); // [{ date, cash, card, transfer, total }]
    const [itemCountsLoading, setItemCountsLoading] = useState(false);
    const [itemCounts, setItemCounts] = useState([]); // [{ platillo, variante, qty }]
    const [calendarTab, setCalendarTab] = useState('cashier'); // 'cashier' | 'items'
    const [showDayPickerModal, setShowDayPickerModal] = useState(false);
    const [expandedItems, setExpandedItems] = useState(() => new Set()); // Set<string> platillo

    // Agrupar por semana con inicio en miércoles
    const getWeekStartWednesday = (dateStr) => {
        // dateStr: 'YYYY-MM-DD'
        const d = new Date(`${dateStr}T00:00:00Z`);
        const dow = d.getUTCDay(); // 0..6 (Dom..Sáb)
        const WED = 3;
        const diff = (dow - WED + 7) % 7;
        const start = new Date(d);
        start.setUTCDate(d.getUTCDate() - diff);
        return start.toISOString().slice(0, 10);
    };
    const addDays = (dateStr, days) => {
        const d = new Date(`${dateStr}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + days);
        return d.toISOString().slice(0, 10);
    };
    const weeklyGroups = (() => {
        const map = new Map(); // weekStart -> group
        for (const r of dailySalesRows) {
            const ws = getWeekStartWednesday(r.date);
            if (!map.has(ws)) {
                map.set(ws, {
                    weekStart: ws,
                    weekEnd: addDays(ws, 6), // Mié..Mar
                    rows: [],
                    totals: { cash: 0, card: 0, transfer: 0, total: 0 }
                });
            }
            const g = map.get(ws);
            g.rows.push(r);
            g.totals.cash += r.cash || 0;
            g.totals.card += r.card || 0;
            g.totals.transfer += r.transfer || 0;
            g.totals.total += r.total || 0;
        }
        // ordenar grupos por semana más reciente -> más antigua
        const groups = Array.from(map.values()).sort((a, b) => (a.weekStart < b.weekStart ? 1 : -1));
        // y dentro de cada grupo, días más recientes -> más antiguos
        groups.forEach(g => g.rows.sort((a, b) => (a.date < b.date ? 1 : -1)));
        return groups;
    })();

    const formatShortDateEs = (dateStr) => {
        // dateStr: YYYY-MM-DD -> "13-Dic"
        const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
        const [y, m, d] = (dateStr || '').split('-').map(n => parseInt(n, 10));
        if (!y || !m || !d) return dateStr;
        return `${d}-${months[m - 1]}`;
    };

    // const formatDebugClock = (iso) => {
    //     if (!iso) return '—';
    //     const d = new Date(iso);
    //     return d.toLocaleString('es-MX', {
    //         year: 'numeric',
    //         month: '2-digit',
    //         day: '2-digit',
    //         hour: '2-digit',
    //         minute: '2-digit',
    //         second: '2-digit',
    //     });
    // };

    const groupedItemCounts = (() => {
        const map = new Map(); // platillo -> { platillo, totalQty, variants: Map(variante -> qty) }
        for (const it of itemCounts) {
            const platillo = it?.platillo || '';
            const variante = it?.variante || '';
            const qty = it?.qty || 0;
            if (!map.has(platillo)) map.set(platillo, { platillo, totalQty: 0, variants: new Map() });
            const g = map.get(platillo);
            g.totalQty += qty;
            g.variants.set(variante, (g.variants.get(variante) || 0) + qty);
        }
        return Array.from(map.values())
            .sort((a, b) => (b.totalQty - a.totalQty))
            .map(g => ({
                platillo: g.platillo,
                totalQty: g.totalQty,
                variants: Array.from(g.variants.entries())
                    .map(([variante, qty]) => ({ variante, qty }))
                    .sort((a, b) => b.qty - a.qty)
            }));
    })();

    const handleDoubleClick = () => {
        setPasswordTarget('calendar');
        setShowPasswordModal(true);
    };

    const handleZonaYummyDoubleClick = () => {
        setPasswordTarget('balance');
        setShowPasswordModal(true);
    };

    // Enfocar automáticamente el input al abrir el modal
    useEffect(() => {
        if (showPasswordModal) {
            // Espera a que el input exista en el DOM
            setTimeout(() => {
                passwordInputRef.current?.focus();
                passwordInputRef.current?.select?.();
            }, 0);
        }
    }, [showPasswordModal]);

    const handlePasswordAccept = () => {
        if (passwordInput === "2on4") {
            if (passwordTarget === 'calendar') {
                // Mostrar modal de calendario de ventas
                setShowCalendarModal(true);
                setSelectedDate(new Date());
                setMonthlyActiveStartDate(new Date());
            } else if (passwordTarget === 'balance') {
                // Mostrar modal de balance
                setShowBalanceModal(true);
                setBalanceDate(new Date());
            }
        }
        setShowPasswordModal(false);
        setPasswordInput("");
    };

    const handlePasswordCancel = () => {
        setShowPasswordModal(false);
        setPasswordInput("");
    };

    const closeCalendarModal = () => {
        setShowCalendarModal(false);
        setSumsLoading(false);
        setSumsProgress({ done: 0, total: 0 });
        setDailySalesRows([]);
        setItemCounts([]);
        setItemCountsLoading(false);
        setCalendarTab('cashier');
        setShowDayPickerModal(false);
        setExpandedItems(new Set());
    };

    const closeBalanceModal = () => {
        setShowBalanceModal(false);
        setBalanceOrders([]);
        setBalanceLoading(false);
        setShowBalanceDayPicker(false);
        setShowEditPaymentsModal(false);
        setEditingOrder(null);
        setEditingPaymentId(null);
    };

    const openEditPaymentsModal = (order) => {
        setEditingOrder(order);
        setShowEditPaymentsModal(true);
        setEditingPaymentId(null);
    };

    const closeEditPaymentsModal = () => {
        setShowEditPaymentsModal(false);
        setEditingOrder(null);
        setEditingPaymentId(null);
    };

    const handleEditPaymentAmount = (pagoId, currentAmount) => {
        setEditingPaymentId(pagoId);
        setEditingPaymentAmount(currentAmount);
    };

    const confirmEditPaymentAmount = async () => {
        if (!editingOrder || !editingPaymentId) return;
        
        const pago = editingOrder.pagos.find(p => p._id === editingPaymentId);
        const confirmMsg = `¿Cambiar monto de $${pago?.monto?.toFixed(2)} a $${editingPaymentAmount.toFixed(2)}?`;
        
        if (!window.confirm(confirmMsg)) return;
        
        try {
            await ordersApi.updatePaymentAmount(editingOrder.OrderID, editingPaymentId, editingPaymentAmount);
            // Refrescar datos
            const offset = -new Date().getTimezoneOffset() / 60;
            const dateStr = toLocalYMD(balanceDate);
            const res = await ordersApi.getOrdersByDate(dateStr, offset);
            setBalanceOrders(res?.orders || []);
            // Actualizar orden en edición
            const updatedOrder = res?.orders?.find(o => o.OrderID === editingOrder.OrderID);
            if (updatedOrder) setEditingOrder(updatedOrder);
            setEditingPaymentId(null);
            notify('Monto actualizado');
        } catch (err) {
            console.error(err);
            notify(`Error al actualizar monto: ${err}`);
        }
    };

    const handleDeletePayment = async (pagoId) => {
        if (!editingOrder) return;
        
        const pago = editingOrder.pagos.find(p => p._id === pagoId);
        const confirmMsg = `¿Eliminar pago de $${pago?.monto?.toFixed(2)}?\n\nEsta acción no se puede deshacer.`;
        
        if (!window.confirm(confirmMsg)) return;
        
        try {
            await ordersApi.deletePayment(editingOrder.OrderID, pagoId);
            // Refrescar datos
            const offset = -new Date().getTimezoneOffset() / 60;
            const dateStr = toLocalYMD(balanceDate);
            const res = await ordersApi.getOrdersByDate(dateStr, offset);
            setBalanceOrders(res?.orders || []);
            // Actualizar orden en edición
            const updatedOrder = res?.orders?.find(o => o.OrderID === editingOrder.OrderID);
            if (updatedOrder) {
                setEditingOrder(updatedOrder);
            } else {
                closeEditPaymentsModal(); // Si ya no hay pagos excedidos, cerrar
            }
            notify('Pago eliminado');
        } catch (err) {
            console.error(err);
            notify(`Error al eliminar pago: ${err}`);
        }
    };

    // useEffect para cargar órdenes cuando cambia la fecha en el modal de balance
    useEffect(() => {
        if (!showBalanceModal) return;
        let cancelled = false;

        const fetchBalanceOrders = async () => {
            try {
                setBalanceLoading(true);
                const offset = -new Date().getTimezoneOffset() / 60;
                const dateStr = toLocalYMD(balanceDate);
                const res = await ordersApi.getOrdersByDate(dateStr, offset);
                if (!cancelled) setBalanceOrders(res?.orders || []);
            } catch (err) {
                console.error(err);
                if (!cancelled) setBalanceOrders([]);
            } finally {
                if (!cancelled) setBalanceLoading(false);
            }
        };

        fetchBalanceOrders();
        return () => { cancelled = true; };
    }, [balanceDate, showBalanceModal]);

    // Evitar "scroll chaining" hacia la pantalla principal cuando el modal llega a su límite
    useEffect(() => {
        const anyModalOpen = showCalendarModal || showPasswordModal || showDayPickerModal || showBalanceModal || showBalanceDayPicker || showEditPaymentsModal;
        if (anyModalOpen) {
            // Guardar estado previo solo la primera vez que bloqueamos
            if (document.body.style.overflow !== 'hidden') {
                bodyScrollLockRef.current = {
                    overflow: document.body.style.overflow || '',
                    paddingRight: document.body.style.paddingRight || ''
                };
            }
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = 'hidden';
            if (scrollbarWidth > 0) {
                document.body.style.paddingRight = `${scrollbarWidth}px`;
            }
            return;
        }

        // Restaurar
        document.body.style.overflow = bodyScrollLockRef.current.overflow;
        document.body.style.paddingRight = bodyScrollLockRef.current.paddingRight;
    }, [showCalendarModal, showPasswordModal, showDayPickerModal]);

    // Estado para detectar dispositivo móvil y actualizar al redimensionar
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // useEffect para obtener sumas diarias al cambiar mes en calendario
    useEffect(() => {
        if (!showCalendarModal) return;
        let cancelled = false;

            const fetchSums = async () => {
                const year = monthlyActiveStartDate.getFullYear();
                const month = monthlyActiveStartDate.getMonth();
                const daysInMonth = new Date(year, month + 1, 0).getDate();
                const offset = -new Date().getTimezoneOffset() / 60;

            setSumsLoading(true);
            setSumsProgress({ done: 0, total: daysInMonth });
            setDailySums({});
            setDailySalesRows([]);

                const sums = {};
            const rows = [];

                for (let d = 1; d <= daysInMonth; d++) {
                if (cancelled) return;
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                    try {
                    const res = await ordersApi.getSumByDateV2Breakdown(dateStr, offset);
                    const total = res?.total || 0;
                    sums[dateStr] = total;
                    if (total > 0) {
                        rows.push({
                            date: res.date || dateStr,
                            cash: res.cash || 0,
                            card: res.card || 0,
                            transfer: res.transfer || 0,
                            total
                        });
                    }
                    } catch (err) {
                        console.error(err);
                        sums[dateStr] = 0;
                } finally {
                    if (!cancelled) {
                        setSumsProgress(prev => ({ done: prev.done + 1, total: daysInMonth }));
                    }
                }
            }

            if (!cancelled) {
                setDailySums(sums);
                // Más reciente -> más antiguo
                rows.sort((a, b) => (a.date < b.date ? 1 : -1));
                setDailySalesRows(rows);
                setSumsLoading(false);
            }
            };

            fetchSums();
        return () => { cancelled = true; };
    }, [monthlyActiveStartDate, showCalendarModal]);

    // Debajo del calendario: conteo de platillos/variantes del día seleccionado
    useEffect(() => {
        if (!showCalendarModal) return;
        let cancelled = false;

        const fetchCounts = async () => {
            try {
                setItemCountsLoading(true);
                const offset = -new Date().getTimezoneOffset() / 60;
                const dateStr = toLocalYMD(selectedDate);
                const res = await ordersApi.getItemCountsByDate(dateStr, offset);
                if (!cancelled) setItemCounts(res?.items || []);
            } catch (err) {
                console.error(err);
                if (!cancelled) setItemCounts([]);
            } finally {
                if (!cancelled) setItemCountsLoading(false);
            }
        };

        if (calendarTab === 'items') fetchCounts();
        return () => { cancelled = true; };
    }, [selectedDate, showCalendarModal, calendarTab]);

    return (
        <div
            className={`container-fluid mesero-orders-shell${modeInterface && galleryView ? ' mesero-orders-shell--gallery' : ''}`}
            style={{background: (reloadFlag && modeInterface) ? 'linear-gradient(to right, #e0f7fa, #b2ebf2)' : 'none'}}
        >
            <ToastContainer position="top-right" autoClose={3000} limit={3} />
            {/* Header y toolbar — solo vista clásica de mesero */}
            {modeInterface && !galleryView && (
                <>
            <div className="row">
                <div className="col-4">
                    <div style={{color: '#000000', textAlign:'left'}}>
                        <Counter30To0 handleReloadFlag={handleReloadFlag}/>
                    </div>
                </div>
                <div className="col-8">
                    <h1 style={{ color: "#000000" }}>
                        <span 
                            onDoubleClick={handleZonaYummyDoubleClick} 
                            style={{ cursor: 'pointer', color: '#166534' }}
                            title="Doble click para ver balance"
                        >ZonaYummy</span>{' '}
                        <span onDoubleClick={handleDoubleClick}>Comandas</span>
                    </h1>
                </div>
            </div>
            <hr style={{backgroundColor:"white"}}/>
            <div>
                <div className="row align-items-center mesero-orders-toolbar">
                    <div className="col-2">
                        <Button variant="success" size="lg" onClick={() => handleSlideChange(slide - 1)} disabled={isMobile}>←</Button>
                    </div>
                    <div className="col-4">
                        <Button variant="success" size="lg" onClick={handleNewOrderClick}>Nueva Orden</Button>
                    </div>
                    <div className="col-4 mesero-orders-toolbar__date">
                        <button
                            type="button"
                            className="zy-date-btn mesero-orders-date-btn"
                            onClick={() => setShowOrdersDayPicker(true)}
                            title="Seleccionar fecha de órdenes"
                        >
                            {formatShortDateEs(toLocalYMD(ordersViewDate))} ▾
                        </button>
                        {!isOrdersViewToday(ordersViewDate) && (
                            <button
                                type="button"
                                className="mesero-orders-date-btn__today"
                                onClick={resetOrdersViewDateToToday}
                                title="Volver al día de hoy"
                            >
                                Hoy
                            </button>
                        )}
                    </div>
                    <div className="col-2">
                        <Button variant="success" size="lg" onClick={() => handleSlideChange(slide + 1)} disabled={isMobile}>→</Button>
                    </div>
                </div>
            </div>
            {/* Debug GET orders panel — disabled, keep for future troubleshooting
            <div className="mesero-orders-fetch-debug">
                ...
            </div>
            */}
                </>
            )}
            {modeInterface && !galleryView && showOrdersDayPicker && (
                <div className="zy-modal-overlay">
                    <div className="zy-modal zy-date-modal">
                        <div className="zy-modal__header">
                            <div>
                                <div className="zy-modal__title">Fecha de órdenes</div>
                                <div className="zy-modal__subtitle">
                                    {toLocalYMD(ordersViewDate)}
                                </div>
                            </div>
                            <button className="zy-modal__close" onClick={() => setShowOrdersDayPicker(false)} title="Cerrar">✕</button>
                        </div>
                        <div className="zy-modal__body">
                            <Calendar
                                onChange={handleOrdersViewDateChange}
                                value={ordersViewDate}
                            />
                        </div>
                        <div className="zy-modal__footer">
                            <button className="zy-btn zy-btn--secondary" onClick={() => setShowOrdersDayPicker(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}
            {modeInterface && !galleryView && (
                <button
                    type="button"
                    className="mesero-view-fab"
                    onClick={() => setGalleryViewPersisted(true)}
                    title="Cambiar a vista galería"
                    aria-label="Cambiar a vista galería"
                >
                    <span className="mesero-view-fab__icon" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M4 6h16v2H4V6zm0 5h10v2H4v-2zm0 5h16v2H4v-2z" />
                        </svg>
                    </span>
                    <span className="mesero-view-fab__label">Galería</span>
                </button>
            )}
            {renderOrders()}
            {showPasswordModal && (
                <div 
                    className="password-modal-overlay" 
                    style={{
                        position: 'fixed', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        backgroundColor: 'rgba(0,0,0,0.5)', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        zIndex: 1000
                    }}
                >
                    <div 
                        className="password-modal" 
                        style={{
                            backgroundColor: '#fff', 
                            padding: '20px', 
                            borderRadius: '5px', 
                            textAlign: 'center'
                        }}
                    >
                        <h2>Ingrese contraseña</h2>
                        <input 
                            ref={passwordInputRef}
                            type="password" 
                            value={passwordInput} 
                            onChange={(e) => setPasswordInput(e.target.value)} 
                            style={{ marginBottom: '10px', width: '100%', padding: '5px' }}
                        />
                        <div>
                            <button onClick={handlePasswordAccept} style={{ marginRight: '10px' }}>Aceptar</button>
                            <button onClick={handlePasswordCancel}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}
            {showCalendarModal && (
                <div className="zy-modal-overlay">
                    <div className="zy-modal zy-calendar-modal">
                        <div className="zy-modal__header">
                            <div>
                                <div className="zy-modal__title">Ventas diarias</div>
                                <div className="zy-modal__subtitle">
                                    {monthlyActiveStartDate.toLocaleString('es-MX', { month: 'long', year: 'numeric' })}
                                </div>
                            </div>
                            <button className="zy-modal__close" onClick={closeCalendarModal} title="Cerrar">✕</button>
                        </div>
                        <div className="zy-modal__body">
                            {sumsLoading && (
                                <div className="zy-progress">
                                    <div className="zy-progress__row">
                                        <div className="zy-progress__label">Calculando sumas del mes…</div>
                                        <div className="zy-progress__count">{sumsProgress.done}/{sumsProgress.total}</div>
                                    </div>
                                    <div className="zy-progress__bar">
                                        <div
                                            className="zy-progress__fill"
                                            style={{ width: `${sumsProgress.total > 0 ? Math.round((sumsProgress.done / sumsProgress.total) * 100) : 0}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="zy-tabs">
                                <button
                                    className={`zy-tab ${calendarTab === 'cashier' ? 'active' : ''}`}
                                    onClick={() => setCalendarTab('cashier')}
                                    type="button"
                                >
                                    Corte de caja
                                </button>
                                <button
                                    className={`zy-tab ${calendarTab === 'items' ? 'active' : ''}`}
                                    onClick={() => setCalendarTab('items')}
                                    type="button"
                                >
                                    Platillos vendidos
                                </button>
                            </div>

                            {calendarTab === 'cashier' && (
                                <div className="zy-sales">
                                    <div className="zy-sales__header">
                                        <div className="zy-sales__title">Resumen por semana (inicio: miércoles)</div>
                                    </div>
                                    <div className="zy-sales__list">
                                        {!sumsLoading && weeklyGroups.length === 0 && (
                                            <div className="zy-sales__empty">No hay ventas en este mes.</div>
                                        )}
                                        {weeklyGroups.map((g) => (
                                            <div key={g.weekStart} className="zy-week">
                                                <div className="zy-week__header">
                                                    <div className="zy-week__left">
                                                        <div className="zy-week__title">Semana</div>
                                                    </div>
                                                    <div className="zy-week__right">
                                                        <div className="zy-week__cols">
                                                            <div className="zy-week__col">💵</div>
                                                            <div className="zy-week__col">💳</div>
                                                            <div className="zy-week__col">📱</div>
                                                            <div className="zy-week__col zy-week__col--total">Σ</div>
                                                        </div>
                                                        <div className="zy-week__totals">
                                                            <div className="zy-week__cell tag--cash">${g.totals.cash.toFixed(2)}</div>
                                                            <div className="zy-week__cell tag--card">${g.totals.card.toFixed(2)}</div>
                                                            <div className="zy-week__cell tag--transfer">${g.totals.transfer.toFixed(2)}</div>
                                                            <div className="zy-week__cell tag--total">${g.totals.total.toFixed(2)}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {g.rows.map((r) => (
                                                    <div key={r.date} className="zy-week__row">
                                                        <div className="zy-week__date">{formatShortDateEs(r.date)}</div>
                                                        <div className="zy-week__grid">
                                                            <div className="zy-week__cell tag--cash">${(r.cash || 0).toFixed(2)}</div>
                                                            <div className="zy-week__cell tag--card">${(r.card || 0).toFixed(2)}</div>
                                                            <div className="zy-week__cell tag--transfer">${(r.transfer || 0).toFixed(2)}</div>
                                                            <div className="zy-week__cell tag--total">${(r.total || 0).toFixed(2)}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {calendarTab === 'cashier' && (
                            <div className={sumsLoading ? 'zy-calendar-disabled' : ''}>
                        <Calendar
                            onChange={setSelectedDate}
                            value={selectedDate}
                            onActiveStartDateChange={({ activeStartDate }) => setMonthlyActiveStartDate(activeStartDate)}
                            tileContent={({ date, view }) => {
                                if (view === 'month') {
                                    const dateStr = toLocalYMD(date);
                                    const sum = dailySums[dateStr] !== undefined ? dailySums[dateStr] : null;
                                    return sum !== null ? <div style={{ fontSize: '0.75em', marginTop: '4px' }}>${sum}</div> : null;
                                }
                            }}
                        />
                            </div>
                            )}

                            {calendarTab === 'items' && (
                                <div className="zy-items">
                                    <div className="zy-items__header">
                                        <div className="zy-items__title">Platillos vendidos</div>
                                        <button
                                            type="button"
                                            className="zy-date-btn"
                                            onClick={() => setShowDayPickerModal(true)}
                                            title="Seleccionar día"
                                        >
                                            {formatShortDateEs(toLocalYMD(selectedDate))} ▾
                                        </button>
                                    </div>
                                    <div className="zy-items__list">
                                        {itemCountsLoading && (
                                            <div className="zy-items__empty">Cargando…</div>
                                        )}
                                        {!itemCountsLoading && groupedItemCounts.length === 0 && (
                                            <div className="zy-items__empty">Sin ventas registradas en este día.</div>
                                        )}
                                        {!itemCountsLoading && groupedItemCounts.map((g, idx) => {
                                            const isOpen = expandedItems.has(g.platillo);
                                            return (
                                                <div key={`${g.platillo}-${idx}`} className="zy-items__group">
                                                    <div className="zy-items__groupHeader">
                                                        <button
                                                            type="button"
                                                            className="zy-items__expandBtn"
                                                            onClick={() => {
                                                                setExpandedItems(prev => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(g.platillo)) next.delete(g.platillo);
                                                                    else next.add(g.platillo);
                                                                    return next;
                                                                });
                                                            }}
                                                            title={isOpen ? 'Contraer' : 'Expandir'}
                                                        >
                                                            {isOpen ? '▾' : '▸'}
                                                        </button>
                                                        <div className="zy-items__groupTitle">{g.platillo}</div>
                                                        <div className="zy-items__qty">x{g.totalQty}</div>
                                                    </div>
                                                    {isOpen && g.variants.map((v, j) => (
                                                        <div key={`${g.platillo}-${v.variante || 'base'}-${j}`} className="zy-items__row">
                                                            <div className="zy-items__name">
                                                                <div className="zy-items__variant zy-items__variant--indented">{v.variante || 'Base'}</div>
                                                            </div>
                                                            <div className="zy-items__qty">x{v.qty}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Modal de selección de día (encima del modal principal) */}
                            {showDayPickerModal && (
                                <div className="zy-modal-overlay zy-modal-overlay--nested">
                                    <div className="zy-modal zy-date-modal">
                                        <div className="zy-modal__header">
                                            <div>
                                                <div className="zy-modal__title">Seleccionar día</div>
                                                <div className="zy-modal__subtitle">
                                                    {toLocalYMD(selectedDate)}
                                                </div>
                                            </div>
                                            <button className="zy-modal__close" onClick={() => setShowDayPickerModal(false)} title="Cerrar">✕</button>
                                        </div>
                                        <div className="zy-modal__body">
                                            <Calendar
                                                onChange={(d) => { setSelectedDate(d); setShowDayPickerModal(false); }}
                                                value={selectedDate}
                                            />
                                        </div>
                                        <div className="zy-modal__footer">
                                            <button className="zy-btn zy-btn--secondary" onClick={() => setShowDayPickerModal(false)}>Cancelar</button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="zy-modal__footer">
                            <button className="zy-btn zy-btn--secondary" onClick={closeCalendarModal}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal de Balance (ZonaYummy) */}
            {showBalanceModal && (() => {
                // Calcular totales desde los datos de órdenes
                const totalVendido = balanceOrders.reduce((sum, o) => sum + (o?.CuentaTotal || 0), 0);
                const totalCobrado = balanceOrders.reduce((sum, o) => {
                    const pagosSum = (o?.pagos || []).reduce((ps, p) => ps + (p?.monto || 0), 0);
                    return sum + pagosSum;
                }, 0);
                const balance = totalCobrado - totalVendido;
                const balanceStatus = balance === 0 ? 'ok' : (balance > 0 ? 'exceeded' : 'pending');

                return (
                    <div className="zy-modal-overlay">
                        <div className="zy-modal zy-balance-modal">
                            <div className="zy-modal__header">
                                <div>
                                    <div className="zy-modal__title">Balance del Día</div>
                                    <div className="zy-modal__subtitle">
                                        {formatShortDateEs(toLocalYMD(balanceDate))}
                                    </div>
                                </div>
                                <button className="zy-modal__close" onClick={closeBalanceModal} title="Cerrar">✕</button>
                            </div>
                            <div className="zy-modal__body">
                                {/* Selector de fecha como botón */}
                                <div className="zy-balance__date-selector">
                                    <button
                                        type="button"
                                        className="zy-date-btn"
                                        onClick={() => setShowBalanceDayPicker(true)}
                                        title="Seleccionar día"
                                    >
                                        {formatShortDateEs(toLocalYMD(balanceDate))} ▾
                                    </button>
                                </div>

                                {balanceLoading && (
                                    <div className="zy-balance__loading">Cargando pedidos...</div>
                                )}

                                {!balanceLoading && balanceOrders.length === 0 && (
                                    <div className="zy-balance__empty">No hay pedidos en este día.</div>
                                )}

                                {/* Lista de pedidos con barras de progreso */}
                                {!balanceLoading && balanceOrders.length > 0 && (
                                    <div className="zy-balance__orders">
                                        <div className="zy-balance__orders-header">
                                            <span>Pedidos del día ({balanceOrders.length})</span>
                                        </div>
                                        <div className="zy-balance__orders-list">
                                            {balanceOrders.map((order) => {
                                                const orderTotal = order?.CuentaTotal || 0;
                                                const orderPagado = (order?.pagos || []).reduce((s, p) => s + (p?.monto || 0), 0);
                                                const orderPendiente = orderTotal - orderPagado;
                                                const orderPercent = orderTotal > 0 
                                                    ? Math.min(100, Math.max(0, (orderPagado / orderTotal) * 100)) 
                                                    : 0;
                                                const orderStatus = orderPendiente === 0 ? 'ok' : (orderPendiente < 0 ? 'exceeded' : 'pending');
                                                const paymentMethods = [...new Set((order?.pagos || []).map(p => p?.metodoPago))];
                                                
                                                return (
                                                    <div key={order.OrderID} className={`zy-balance__order-item zy-balance__order-item--${orderStatus}`}>
                                                        <div className="zy-balance__order-row">
                                                            <span className="zy-balance__order-id">#{order.OrderID}</span>
                                                            <span className="zy-balance__order-methods">
                                                                {paymentMethods.map(m => m === 'card' ? '\u{1F4B3}' : m === 'transfer' ? '\u{1F4F1}' : '\u{1F4B5}').join('')}
                                                            </span>
                                                            <span className="zy-balance__order-total">${orderTotal.toFixed(2)}</span>
                                                            <span className={`zy-balance__order-status zy-balance__order-status--${orderStatus}`}>
                                                                {orderStatus === 'ok' ? 'OK' : orderStatus === 'exceeded' ? 'Exced.' : 'Pend.'}
                                                            </span>
                                                            {orderStatus === 'exceeded' && (
                                                                <button
                                                                    className="zy-balance__order-menu"
                                                                    onClick={() => openEditPaymentsModal(order)}
                                                                    title="Editar pagos"
                                                                >
                                                                    ⋮
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className={`zy-balance__order-bar zy-balance__order-bar--${orderStatus}`}>
                                                            <div className="zy-balance__order-fill" style={{ width: `${orderPercent}%` }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Resumen de totales */}
                                {!balanceLoading && balanceOrders.length > 0 && (
                                    <div className="zy-balance__summary">
                                        <div className="zy-balance__summary-row">
                                            <span className="zy-balance__summary-label">Total Vendido:</span>
                                            <span className="zy-balance__summary-value">${totalVendido.toFixed(2)}</span>
                                        </div>
                                        <div className="zy-balance__summary-row">
                                            <span className="zy-balance__summary-label">Total Cobrado:</span>
                                            <span className="zy-balance__summary-value">${totalCobrado.toFixed(2)}</span>
                                        </div>
                                        <div className={`zy-balance__summary-row zy-balance__summary-row--balance zy-balance__summary-row--${balanceStatus}`}>
                                            <span className="zy-balance__summary-label">Balance:</span>
                                            <span className="zy-balance__summary-value">
                                                {balance === 0 ? '$0.00' : (balance > 0 ? `+$${balance.toFixed(2)}` : `-$${Math.abs(balance).toFixed(2)}`)}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Modal de selección de día (encima del modal de balance) */}
                                {showBalanceDayPicker && (
                                    <div className="zy-modal-overlay zy-modal-overlay--nested">
                                        <div className="zy-modal zy-date-modal">
                                            <div className="zy-modal__header">
                                                <div>
                                                    <div className="zy-modal__title">Seleccionar día</div>
                                                    <div className="zy-modal__subtitle">
                                                        {toLocalYMD(balanceDate)}
                                                    </div>
                                                </div>
                                                <button className="zy-modal__close" onClick={() => setShowBalanceDayPicker(false)} title="Cerrar">✕</button>
                                            </div>
                                            <div className="zy-modal__body">
                                                <Calendar
                                                    onChange={(d) => { setBalanceDate(d); setShowBalanceDayPicker(false); }}
                                                    value={balanceDate}
                                                />
                                            </div>
                                            <div className="zy-modal__footer">
                                                <button className="zy-btn zy-btn--secondary" onClick={() => setShowBalanceDayPicker(false)}>Cancelar</button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Submodal de edición de pagos (encima del modal de balance) */}
                                {showEditPaymentsModal && editingOrder && (
                                    <div className="zy-modal-overlay zy-modal-overlay--nested" onClick={(e) => e.target === e.currentTarget && e.stopPropagation()}>
                                        <div className="zy-modal zy-edit-payments-modal">
                                            <div className="zy-modal__header zy-modal__header--danger">
                                                <div>
                                                    <div className="zy-modal__title">Editar Pagos</div>
                                                    <div className="zy-modal__subtitle">
                                                        Orden #{editingOrder.OrderID}
                                                    </div>
                                                </div>
                                                <button className="zy-modal__close" onClick={closeEditPaymentsModal} title="Cerrar">✕</button>
                                            </div>
                                            <div className="zy-modal__body">
                                                <div className="zy-edit-payments__info">
                                                    <span>Total orden: <b>${editingOrder.CuentaTotal?.toFixed(2)}</b></span>
                                                    <span>Cobrado: <b>${(editingOrder.pagos || []).reduce((s, p) => s + (p?.monto || 0), 0).toFixed(2)}</b></span>
                                                </div>

                                                <div className="zy-edit-payments__list">
                                                    {(editingOrder.pagos || []).map((pago, idx) => (
                                                        <div key={pago._id} className="zy-edit-payments__item">
                                                            <div className="zy-edit-payments__item-info">
                                                                <span className="zy-edit-payments__item-idx">#{idx + 1}</span>
                                                                <span className="zy-edit-payments__item-date">
                                                                    {new Date(pago.fecha).toLocaleString('es-MX', { 
                                                                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                                                                    })}
                                                                </span>
                                                                <span className="zy-edit-payments__item-method">
                                                                    {pago.metodoPago === 'card' ? '💳' : pago.metodoPago === 'transfer' ? '📱' : '💵'}
                                                                </span>
                                                            </div>
                                                            <div className="zy-edit-payments__item-amount">
                                                                {editingPaymentId === pago._id ? (
                                                                    <div className="zy-edit-payments__edit-input">
                                                                        <span>$</span>
                                                                        <input
                                                                            type="number"
                                                                            value={editingPaymentAmount}
                                                                            onChange={(e) => setEditingPaymentAmount(parseFloat(e.target.value) || 0)}
                                                                            min="0"
                                                                            step="0.01"
                                                                            autoFocus
                                                                        />
                                                                        <button 
                                                                            className="zy-edit-payments__btn zy-edit-payments__btn--confirm"
                                                                            onClick={confirmEditPaymentAmount}
                                                                            title="Confirmar"
                                                                        >
                                                                            ✓
                                                                        </button>
                                                                        <button 
                                                                            className="zy-edit-payments__btn zy-edit-payments__btn--cancel"
                                                                            onClick={() => setEditingPaymentId(null)}
                                                                            title="Cancelar"
                                                                        >
                                                                            ✕
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <span className="zy-edit-payments__item-monto">${pago.monto?.toFixed(2)}</span>
                                                                        <button 
                                                                            className="zy-edit-payments__btn zy-edit-payments__btn--edit"
                                                                            onClick={() => handleEditPaymentAmount(pago._id, pago.monto)}
                                                                            title="Editar monto"
                                                                        >
                                                                            ✏️
                                                                        </button>
                                                                        <button 
                                                                            className="zy-edit-payments__btn zy-edit-payments__btn--delete"
                                                                            onClick={() => handleDeletePayment(pago._id)}
                                                                            title="Eliminar pago"
                                                                        >
                                                                            🗑️
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {(editingOrder.pagos || []).length === 0 && (
                                                    <div className="zy-edit-payments__empty">No hay pagos registrados.</div>
                                                )}
                                            </div>
                                            <div className="zy-modal__footer">
                                                <button className="zy-btn zy-btn--secondary" onClick={closeEditPaymentsModal}>Cerrar</button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="zy-modal__footer">
                                <button className="zy-btn zy-btn--secondary" onClick={closeBalanceModal}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    )
}

export default MeseroOrdersShell
